import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { EntityKind } from "../../Dashboard/types";

// Wire types for POST /api/v2/rms/import (#10850).

export interface ImportMessage {
  severity: "warning" | "error";
  column: string | null;
  text: string;
}

export interface ImportColumn {
  header: string;
  attribute: string | null;
  match: string;
  note: string | null;
}

export interface ImportRow {
  row_number: number;
  name: string;
  status: "ok" | "warning" | "error" | "update";
  messages: ImportMessage[];
  values: Record<string, unknown[]>;
  created_entity_id: string | null;
  updated_entity_id: string | null;
}

export interface ImportSheet {
  sheet_name: string | null;
  kind: string | null;
  ignored: boolean;
  errors: string[];
  columns: ImportColumn[];
  rows: ImportRow[];
}

export interface ImportSummaryGroup {
  kind: string;
  name: string;
  rows: number;
  row_numbers: number[];
  managers: string[];
  outcome: "create" | "renamed" | "merged";
}

export interface ImportSummary {
  references_to_create: ImportSummaryGroup[];
  shared_names: ImportSummaryGroup[];
}

export interface ImportResponse {
  sheets: ImportSheet[];
  committed: boolean;
  counts: Record<string, number>;
  summary: ImportSummary;
}

export interface ImportOptions {
  kind?: EntityKind | null;
  create?: boolean;
  mapping?: Record<string, string>;
}

export async function downloadImportTemplate(
  kind: EntityKind | null
): Promise<Blob> {
  const suffix = kind ? `?kind=${kind}` : "";
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/import/template${suffix}`,
    { method: "GET" }
  );
  if (!response.ok) {
    throw new Error("Could not generate the template");
  }
  return response.blob();
}

export async function importSpreadsheet(
  file: File,
  options: ImportOptions
): Promise<ImportResponse> {
  const form = new FormData();
  form.append("uploaded_file", file);
  if (options.kind) {
    form.append("kind", options.kind);
  }
  if (options.mapping) {
    form.append("mapping", JSON.stringify(options.mapping));
  }
  const suffix = options.create ? "?create=true" : "";
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/rms/import${suffix}`,
    { method: "POST", body: form }
  );
  if (!response.ok) {
    const body: { detail?: string } = await response
      .json()
      .catch(() => ({}) as { detail?: string });
    throw new Error(body.detail ?? "The import request failed");
  }
  return response.json() as Promise<ImportResponse>;
}
